import { useCallback, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import useOrgStorageKey from '../../hooks/useOrgStorageKey';
import normalizeSearchTerm from './normalizeSearchTerm';
import { SearchDestination } from './types';

// The search term is always kept in the `search` query param.
export const SEARCH_PARAM = 'search';

// Builds the URL for a destination with the term applied. Params the destination marks `null`
// must be absent, so they are simply never written.
const withSearchTerm = ({ pathname, params }: SearchDestination, term: string) => {
    const query = new URLSearchParams();

    Object.entries(params ?? {}).forEach(([key, value]) => value && query.set(key, value));
    query.set(SEARCH_PARAM, term);

    return `${pathname}?${query.toString()}`;
};

type Options = {
    removeQueryParamsOnSearch: string[];
    filterParams?: string[];
    // See `SearchProps.searchTo` — set means jump mode.
    searchTo?: string;
};

export type SearchController = {
    // The term the field is expected to show for the current location. In filter
    // mode that's whatever is in the URL; in jump mode it's always empty, so
    // landing on a new page resets the field.
    syncedSearch: string;
    // Identifies the page the field belongs to, so the field re-syncs on navigation.
    pathname: string;
    submit: (values: Record<string, string>) => void;
    // Reported by the dropdown as its results change. Read at submit time, so the two submit
    // paths — Enter via the form, "Show all results" via SearchBar — always agree on where to go.
    setTopSuggestion: (showAllTo?: SearchDestination) => void;
    reset: () => void;
};

// Owns everything the search field does to the URL. Two modes:
//
// - filter mode (list pages, the default): the term lives in this page's
//   `?search=` param and submitting rewrites it.
// - jump mode (`searchTo`, detail pages): the term is transient and submitting
//   hands it to the target list page instead of filtering the current one.
const useSearchController = ({
    removeQueryParamsOnSearch,
    filterParams,
    searchTo
}: Options): SearchController => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const orgStorageKey = useOrgStorageKey();

    // A filter with `persistToLocalStorage` restores its selection the moment its
    // URL param goes missing (see ListFilter), so dropping a param has to drop the
    // stored copy too — under the same org-namespaced key ListFilter writes it to.
    // Params that don't persist just no-op here.
    const clearPersistedFilters = useCallback(
        (params: string[]) => {
            params.forEach((param) => localStorage.removeItem(orgStorageKey(param)));
        },
        [orgStorageKey]
    );

    // Whether a destination names the page and list we are already looking at.
    const isCurrentDestination = useCallback(
        ({ pathname: targetPath, params }: SearchDestination) =>
            targetPath === pathname &&
            Object.entries(params ?? {}).every(
                ([key, value]) => (searchParams.get(key) || null) === value
            ),
        [pathname, searchParams]
    );

    // Held in a ref, not state: nothing renders it, so a new value per debounce tick must not
    // re-render the field.
    const showAllToRef = useRef<SearchDestination | undefined>(undefined);
    const setTopSuggestion = useCallback((showAllTo?: SearchDestination) => {
        showAllToRef.current = showAllTo;
    }, []);

    const submit = useCallback(
        (values: Record<string, string>) => {
            const value = normalizeSearchTerm(values[SEARCH_PARAM] ?? '');
            // The top suggestion's list outranks `searchTo`, so a full search lands where the best
            // match actually is. Nothing typed means no destination — don't navigate off the page
            // just because the field was submitted empty.
            const target = value
                ? (showAllToRef.current ?? (searchTo ? { pathname: searchTo } : undefined))
                : undefined;

            // Pointing at the list that is already open is not a navigation: fall through to the
            // rewrite below so the list's own filters and sort survive the search.
            if (target && !isCurrentDestination(target)) {
                // We're leaving this list behind, so the persisted copies of the filters a fresh
                // search drops have to go too — ListFilter restores each one as soon as its param
                // goes missing. Params that don't persist no-op.
                clearPersistedFilters(removeQueryParamsOnSearch);
                navigate(withSearchTerm(target, value));
                return;
            }

            // Jump mode owns nothing in the current page's URL, so there is nothing to clear.
            if (searchTo) {
                return;
            }

            clearPersistedFilters(removeQueryParamsOnSearch);

            setSearchParams((prevState) => {
                removeQueryParamsOnSearch.forEach((param) => prevState.delete(param));
                if (value) {
                    prevState.set(SEARCH_PARAM, value);
                } else {
                    prevState.delete(SEARCH_PARAM);
                }
                return prevState;
            });
        },
        [
            clearPersistedFilters,
            searchTo,
            navigate,
            setSearchParams,
            removeQueryParamsOnSearch,
            isCurrentDestination
        ]
    );

    // Reset everything this field owns: the search term, every active filter
    // param, and the usual pagination cleanup — in the URL and in localStorage.
    // Jump mode owns nothing in the URL, so clearing the field is the whole job.
    const reset = useCallback(() => {
        if (searchTo) {
            return;
        }
        const dropped = [...removeQueryParamsOnSearch, ...(filterParams ?? []), SEARCH_PARAM];
        clearPersistedFilters(dropped);
        setSearchParams((prevState) => {
            dropped.forEach((param) => prevState.delete(param));
            return prevState;
        });
    }, [searchTo, clearPersistedFilters, setSearchParams, removeQueryParamsOnSearch, filterParams]);

    return {
        syncedSearch: searchTo ? '' : (searchParams.get(SEARCH_PARAM)?.trim() ?? ''),
        pathname,
        submit,
        setTopSuggestion,
        reset
    };
};

export default useSearchController;
